import { useInfiniteQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { formatMoney } from '@nokowazzi/types';
import type { CurrencyCode } from '@nokowazzi/types';
import { Button, EmptyState, Select, Spinner } from '@nokowazzi/ui';
import { Table, Td, Th, Thead, Tr } from '../components/DataTable';
import { PageHeader } from '../components/PageHeader';
import type { AdminDisputeListItem, AdminDisputeListResponse } from '../features/disputes/queries';
import {
  DISPUTE_REASON_LABEL,
  DISPUTE_STATUS_FILTER_OPTIONS,
  DisputeStatusBadge,
  isOpenDispute,
  slaRemaining,
  toDisputeStatus,
} from '../features/disputes/status';
import { ApiError, apiFetch } from '../lib/api';
import { formatDateTime } from '../lib/datetime';

export const Route = createFileRoute('/_admin/disputes')({
  component: DisputesScreen,
});

function DisputesScreen() {
  const [statusFilter, setStatusFilter] = useState('active');

  const listQuery = useInfiniteQuery({
    queryKey: ['admin', 'disputes', 'list', { status: statusFilter }] as const,
    queryFn: ({ pageParam }) =>
      apiFetch<AdminDisputeListResponse>('/v1/admin/disputes', {
        query: {
          status: statusFilter === 'all' ? undefined : statusFilter,
          cursor: pageParam ?? undefined,
        },
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  });

  const items: AdminDisputeListItem[] = listQuery.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <>
      <PageHeader title="분쟁 관리" description="에스크로 분쟁 접수 및 해결" />

      <div className="mb-4 flex items-center gap-2">
        <Select
          className="w-48"
          aria-label="상태 필터"
          options={DISPUTE_STATUS_FILTER_OPTIONS}
          value={statusFilter}
          onValueChange={setStatusFilter}
        />
      </div>

      {listQuery.isPending ? (
        <div className="flex min-h-52 items-center justify-center">
          <Spinner label="분쟁 목록 불러오는 중" />
        </div>
      ) : listQuery.isError ? (
        <EmptyState
          title="분쟁 목록을 불러오지 못했습니다"
          description={
            listQuery.error instanceof ApiError ? listQuery.error.message : '잠시 후 다시 시도해 주세요'
          }
          action={
            <Button variant="tertiary" size="sm" onClick={() => void listQuery.refetch()}>
              다시 시도
            </Button>
          }
        />
      ) : items.length === 0 ? (
        <EmptyState
          icon={<ShieldCheck size={40} strokeWidth={1.5} />}
          title="분쟁이 없습니다"
          description="조건에 맞는 분쟁이 없습니다"
        />
      ) : (
        <>
          <Table>
            <Thead>
              <tr>
                <Th className="w-28">주문번호</Th>
                <Th className="w-24">사유</Th>
                <Th className="w-28">상태</Th>
                <Th>구매자</Th>
                <Th>판매자</Th>
                <Th className="w-32 text-right">금액</Th>
                <Th className="w-40">SLA / 접수일</Th>
              </tr>
            </Thead>
            <tbody>
              {items.map((dispute) => {
                const sla = isOpenDispute(toDisputeStatus(dispute.status))
                  ? slaRemaining(dispute.created_at)
                  : null;
                return (
                  <Tr key={dispute.id} className="hover:bg-subtle">
                    <Td>
                      <Link
                        to="/disputes/$id"
                        params={{ id: dispute.id }}
                        className="font-mono text-caption1 font-medium text-fg-brand hover:underline"
                      >
                        {dispute.order_id.slice(0, 8)}
                      </Link>
                    </Td>
                    <Td className="text-fg-secondary">
                      {DISPUTE_REASON_LABEL[dispute.reason_code] ?? dispute.reason_code}
                    </Td>
                    <Td>
                      <DisputeStatusBadge status={dispute.status} />
                    </Td>
                    <Td className="text-fg-secondary">{dispute.buyer?.display_name ?? '—'}</Td>
                    <Td className="text-fg-secondary">{dispute.seller?.display_name ?? '—'}</Td>
                    <Td className="text-right tabular-nums">
                      {dispute.order != null
                        ? formatMoney({
                            amount: dispute.order.total_amount,
                            currency: dispute.order.currency as CurrencyCode,
                          })
                        : '—'}
                    </Td>
                    <Td>
                      {sla != null ? (
                        <span
                          className={
                            sla.overdue
                              ? 'text-caption1 font-semibold text-danger-600'
                              : 'text-caption1 text-fg-secondary'
                          }
                        >
                          {sla.label}
                        </span>
                      ) : (
                        <span className="text-caption2 text-fg-tertiary">
                          {formatDateTime(dispute.created_at)}
                        </span>
                      )}
                    </Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>

          {listQuery.hasNextPage && (
            <div className="mt-4 flex justify-center">
              <Button
                variant="tertiary"
                size="sm"
                loading={listQuery.isFetchingNextPage}
                onClick={() => void listQuery.fetchNextPage()}
              >
                더 보기
              </Button>
            </div>
          )}
        </>
      )}
    </>
  );
}
